import { useMemo } from "react";
import { useRouteLoaderData } from "@remix-run/react";

import { useUserContext } from "~/hooks/useUserContext";
import { AudioPlayer } from "~/components/audio/AudioPlayer";
import { MediaType } from "~/graphql/types";
import { track } from "~/utils/track";
import { getMediafromData } from "~/utils/data";

import type { RefObject } from "react";
import type { SessionRouteLoaderData } from "./route";
import type { MediaPlayerContextValue } from "~/components/media/context";

export interface SessionAudioPlayerProps {
  isDesktopLayout?: boolean;
  playerContextRef: RefObject<MediaPlayerContextValue>;
  startTime: number;
  onEnded?: () => void;
  onReplay: () => void;
  onTimeUpdate: (timestamp: number) => void;
}

export const SessionAudioPlayer = ({
  isDesktopLayout,
  playerContextRef,
  startTime,
  onEnded,
  onReplay,
  onTimeUpdate,
}: SessionAudioPlayerProps) => {
  const { preferences, setPreference } = useUserContext();

  const data = useRouteLoaderData(
    "routes/$class_.sessions.$session",
  ) as SessionRouteLoaderData;

  const src: string | undefined = useMemo(() => {
    // Look for audio asset first, with video asset as a fallback
    const node =
      getMediafromData(data.session.media?.nodes, MediaType.SessionAudio) ??
      getMediafromData(data.session.media?.nodes, MediaType.SessionVideo);

    return node?.url;
  }, [data.session.media?.nodes]);

  if (!src) {
    return null;
  }

  function handleRateChange(playbackRate: number) {
    setPreference("playbackRate", playbackRate);
  }

  function handleVolumeChange(volume: number) {
    setPreference("volume", volume);
  }

  function handleCaptionChange(captionsEnabled: boolean) {
    setPreference("captionsEnabled", captionsEnabled);
  }

  function handleAirplayingChange(isAirplaying: boolean) {
    if (isAirplaying) {
      track("crAirplaySession", {
        className: data.session.class?.slug,
        sessionNumber: data.session.position,
        trigger: "sessionAudioPlayer",
      });
    }
  }

  return (
    <AudioPlayer
      autoplay
      captionsEnabled={preferences.captionsEnabled}
      isDesktopLayout={isDesktopLayout}
      chapters={data.chapters}
      contextRef={playerContextRef}
      src={src}
      captions={[
        { lang: "en", isDefault: true, src: data.session.captionsUrl! },
      ]}
      playbackRate={preferences.playbackRate}
      volume={preferences.volume}
      startTime={startTime}
      title={data.session.title}
      onAirplayingChange={handleAirplayingChange}
      onCaptionChange={handleCaptionChange}
      onEnded={onEnded}
      onRateChange={handleRateChange}
      onVolumeChange={handleVolumeChange}
      onReplay={onReplay}
      onTimeUpdate={onTimeUpdate}
    />
  );
};
